import { z } from "@/zod";
import {
  departmentId,
  projectId,
  roleId,
  storeId,
} from "@/schemas/commonFields";
import { readStore } from "./store";
import i18next from "i18next";
import { createNonNegativeDecimalStringSchema } from "./commonFields/numberSchema";

const id = z.string().length(26);
const store_id = storeId;
const department_id = departmentId.optional();
const project_role_id = roleId.optional();
const project_role = z.string().optional();
const project_role_or_department_id = z.string();

// Custom number schema just for project limits
const projectLimitDecimalStringSchema = createNonNegativeDecimalStringSchema(3);

export const projectLimit = z.object({
  ...readStore.unwrap().shape,
  id,
  project_id: projectId,
  store_id: z.string().length(26),
});

// -------------------------------------------------------------------------------------------------
// Project limit with department
// -------------------------------------------------------------------------------------------------

export const projectLimitWithDepartment = projectLimit.extend({
  department_id: department_id,
  department_name: z.string().optional(),
  hours: projectLimitDecimalStringSchema,
  rate: projectLimitDecimalStringSchema,
  addition: projectLimitDecimalStringSchema,
});

export const readProjectLimitWithDepartment =
  projectLimitWithDepartment.readonly();

export const displayLimitWithDepartment = projectLimitWithDepartment
  .omit({
    id: true,
    department_id: true,
    project_id: true,
  })
  .readonly();

// -------------------------------------------------------------------------------------------------
// Project limit with role
// -------------------------------------------------------------------------------------------------

export const projectLimitWithRole = projectLimit.extend({
  project_role_id,
  project_role,
  hours: projectLimitDecimalStringSchema,
  rate: projectLimitDecimalStringSchema,
  addition: projectLimitDecimalStringSchema,
});

export const readProjectLimitWithRole = projectLimitWithRole.readonly();

export const displayProjectLimitWithRole = projectLimitWithRole
  .omit({
    id: true,
    project_id: true,
  })
  .readonly();

// -------------------------------------------------------------------------------------------------
// Project Limit Write
// -------------------------------------------------------------------------------------------------

export const writeProjectLimit = z.object({
  period: z.string().date(),
  stores: z.array(
    z.object({
      store_id,
      limits: z.array(
        z.object({
          dependency_id: project_role_or_department_id,
          hours: projectLimitDecimalStringSchema,
          rate: projectLimitDecimalStringSchema,
          entry_id: id.optional(),
          addition: projectLimitDecimalStringSchema,
        }),
      ),
    }),
  ),
});

// -------------------------------------------------------------------------------------------------
// Project Limit Period
// -------------------------------------------------------------------------------------------------

export const writeProjectLimitPeriod = z.object({
  from: z.string().date(),
  to: z.string().date(),
  entityId: project_role_or_department_id,
  hours: projectLimitDecimalStringSchema,
  rate: projectLimitDecimalStringSchema,
  addition: projectLimitDecimalStringSchema,
});

// -------------------------------------------------------------------------------------------------
// Project Limit Summary
// -------------------------------------------------------------------------------------------------

export const projectLimitsPeriodsSummary = z
  .object({
    id: id,
    period: z.string().date(),
    hours: projectLimitDecimalStringSchema,
    avg_rate: projectLimitDecimalStringSchema,
    profit: projectLimitDecimalStringSchema,
  })
  .readonly();

export const projectLimitsStoreHours = readStore
  .unwrap()
  .extend({
    hours: z.record(z.string().date(), projectLimitDecimalStringSchema),
  })
  .readonly();

// -------------------------------------------------------------------------------------------------
// Project Limit Upsert
// -------------------------------------------------------------------------------------------------

export const upsertProjectLimit = z
  .object({
    from: z.string().date(),
    to: z.string().date(),
    entityId: project_role_or_department_id,
    entityName: z.string().optional(),
    hours: projectLimitDecimalStringSchema,
    rate: projectLimitDecimalStringSchema,
    addition: projectLimitDecimalStringSchema,
    period: z.string().date(),
  })
  .superRefine((data, ctx) => {
    // Validate entityId is not empty
    if (data.entityId === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t("projectLimits.periodsDialog.errors.entityId"),
        path: ["entityId"],
      });
    }
    if (data.to < data.from) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18next.t(
          "projectLimits.periodsDialog.errors.toEarlierThanFrom",
        ),
        path: ["to"],
      });
    }
  });
